import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { useTheme } from '../../../contexts/ThemeContext';
import { useTranslation } from 'react-i18next';
import { 
  Plus, 
  Search, 
  Filter, 
  Star,
  Eye,
  Edit,
  Trash2,
  Copy,
  BarChart3,
  Dumbbell,
  Clock,
  Target,
  TrendingUp,
  Users
} from 'lucide-react';
import IOSModal from '../../ui/IOSModal';
import CreateTemplateModal from './CreateTemplateModal';
import EditTemplateModal from './EditTemplateModal';
import TemplatePreviewModal from './TemplatePreviewModal';
import TemplateStatsModal from './TemplateStatsModal';
import AssignTemplateModal from './AssignTemplateModal';
import TrialStarterTemplatesSection from './TrialStarterTemplatesSection';

export default function TemplatesManager() {
  const { getAuthenticatedApi } = useAuth();
  const { isDark } = useTheme();
  const { t } = useTranslation();
  const api = getAuthenticatedApi();

  // Stati
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [filterDifficulty, setFilterDifficulty] = useState('');
  const [filterGoal, setFilterGoal] = useState('');
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  // Modali
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [modalOrigin, setModalOrigin] = useState({ x: 0, y: 0 });

  // Carica dati
  useEffect(() => {
    fetchTemplates();
    fetchGroups();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      
      const params = new URLSearchParams({
        action: 'list'
      });
      
      if (filterGroup) params.append('group_id', filterGroup);
      if (filterDifficulty) params.append('difficulty', filterDifficulty);
      if (filterGoal) params.append('goal', filterGoal);
      if (showFavoritesOnly) params.append('favorites_only', 'true');
      if (searchQuery) params.append('search', searchQuery);
      
      const response = await api.get(`/gym_workout_templates.php?${params}`);
      
      // L'API restituisce direttamente l'array di template
      if (Array.isArray(response.data)) {
        setTemplates(response.data);
      } else {
        console.error('Formato risposta API non valido:', response.data);
        setTemplates([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento template:', error);
      setTemplates([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchGroups = async () => {
    try {
      const response = await api.get('/gym_template_groups.php?action=list');
      
      // L'API restituisce direttamente l'array di gruppi
      if (Array.isArray(response.data)) {
        setGroups(response.data);
      } else {
        console.error('Formato risposta gruppi non valido:', response.data);
        setGroups([]);
      }
    } catch (error) {
      console.error('Errore nel caricamento gruppi:', error);
      setGroups([]);
    }
  };

  // Handlers
  const handleTemplateCreated = () => {
    setShowCreateModal(false);
    fetchTemplates();
  };

  const handleTemplateUpdated = () => {
    setShowEditModal(false);
    setSelectedTemplate(null);
    fetchTemplates();
  };

  const handleEdit = async (template) => {
    try {
      // Carica dettagli completi con esercizi
      const response = await api.get(`/gym_workout_templates.php?action=get&id=${template.id}`);
      
      // L'API restituisce direttamente l'oggetto template
      if (response.data && response.data.id) {
        setSelectedTemplate(response.data);
        setShowEditModal(true);
      } else {
        console.error('Formato risposta API non valido:', response.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento dettagli template:', error);
    }
  };

  const handlePreview = async (template) => {
    try {
      // Carica dettagli completi
      const response = await api.get(`/gym_workout_templates.php?action=get&id=${template.id}`);
      
      // L'API restituisce direttamente l'oggetto template
      if (response.data && response.data.id) {
        setSelectedTemplate(response.data);
        setShowPreviewModal(true);
      } else {
        console.error('Formato risposta API non valido:', response.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento dettagli:', error);
    }
  };

  const handleStats = (template) => {
    setSelectedTemplate(template);
    setShowStatsModal(true);
  };

  const handleAssign = (template) => {
    setSelectedTemplate(template);
    setShowAssignModal(true);
  };

  const handleAssignSuccess = () => {
    setShowAssignModal(false);
    setSelectedTemplate(null);
    fetchTemplates(); // Ricarica per aggiornare times_used
  };

  const handleDelete = async (template) => {
    if (!window.confirm(`Sei sicuro di voler eliminare il template "${template.name}"?`)) {
      return;
    }

    try {
      const response = await api.delete(`/gym_workout_templates.php?action=delete&id=${template.id}`);
      if (response.data.success) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Errore nell\'eliminazione:', error);
      toast.error('Errore nell\'eliminazione del template');
    }
  };

  const handleDuplicate = async (template) => {
    try {
      const response = await api.post(`/gym_workout_templates.php?action=duplicate&id=${template.id}`);
      if (response.data.success) {
        fetchTemplates();
        toast.success('Template duplicato con successo!');
      }
    } catch (error) {
      console.error('Errore nella duplicazione:', error);
      toast.error('Errore nella duplicazione del template');
    }
  };

  const handleToggleFavorite = async (template) => {
    try {
      const response = await api.post(`/gym_workout_templates.php?action=toggle_favorite&id=${template.id}`);
      if (response.data.success) {
        fetchTemplates();
      }
    } catch (error) {
      console.error('Errore nell\'aggiornamento preferito:', error);
    }
  };

  // Apply filters
  useEffect(() => {
    fetchTemplates();
  }, [filterGroup, filterDifficulty, filterGoal, showFavoritesOnly, searchQuery]);

  const getDifficultyBadge = (level) => {
    const colors = {
      beginner: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      intermediate: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300',
      advanced: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      expert: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
    };
    
    const labels = {
      beginner: 'Principiante',
      intermediate: 'Intermedio',
      advanced: 'Avanzato',
      expert: 'Esperto'
    };
    
    return (
      <span className={`px-2 py-1 text-xs font-medium rounded-full ${colors[level]}`}>
        {labels[level]}
      </span>
    );
  };

  const getGoalIcon = (goal) => {
    switch (goal) {
      case 'strength': return '💪';
      case 'hypertrophy': return '🔥';
      case 'endurance': return '🏃';
      case 'flexibility': return '🧘';
      default: return '🎯';
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
              📋 Template Schede
            </h1>
            <p className="text-gray-600 dark:text-white/65 mt-1">
              Gestisci i template riutilizzabili della palestra
            </p>
          </div>
          
          <button
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              setModalOrigin({ 
                x: rect.left + rect.width / 2, 
                y: rect.top + rect.height / 2 
              });
              setShowCreateModal(true);
            }}
            className="flex items-center rounded-lg bg-indigo-600 px-4 py-2 text-white transition-colors hover:bg-indigo-700 dark:bg-indigo-600 dark:text-white dark:hover:brightness-110"
          >
            <Plus className="h-5 w-5 mr-2" />
            Nuovo Template
          </button>
        </div>

        <TrialStarterTemplatesSection onImported={fetchTemplates} />

        {/* Statistiche rapide */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <StatCard
            icon={<Dumbbell className="h-5 w-5" />}
            label="Template Totali"
            value={templates.length}
            color="blue"
          />
          <StatCard
            icon={<Star className="h-5 w-5" />}
            label="Preferiti"
            value={templates.filter(t => t.is_favorite).length}
            color="yellow"
          />
          <StatCard
            icon={<TrendingUp className="h-5 w-5" />}
            label="Utilizzi Totali"
            value={templates.reduce((sum, t) => sum + parseInt(t.times_used || 0), 0)}
            color="green"
          />
          <StatCard
            icon={<Clock className="h-5 w-5" />}
            label="Durata Media"
            value={templates.length > 0 ? Math.round(templates.reduce((sum, t) => sum + parseInt(t.estimated_duration || 0), 0) / templates.length) + ' min' : '0 min'}
            color="purple"
          />
        </div>

        {/* Filtri */}
        <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-4">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {/* Ricerca */}
            <div className="md:col-span-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  placeholder="Cerca template..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Gruppo */}
            <select
              value={filterGroup}
              onChange={(e) => setFilterGroup(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Tutti i gruppi</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>{group.name}</option>
              ))}
            </select>

            {/* Difficoltà */}
            <select
              value={filterDifficulty}
              onChange={(e) => setFilterDifficulty(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Tutte le difficoltà</option>
              <option value="beginner">Principiante</option>
              <option value="intermediate">Intermedio</option>
              <option value="advanced">Avanzato</option>
              <option value="expert">Esperto</option>
            </select>

            {/* Obiettivo */}
            <select
              value={filterGoal}
              onChange={(e) => setFilterGoal(e.target.value)}
              className="px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
            >
              <option value="">Tutti gli obiettivi</option>
              <option value="strength">Forza</option>
              <option value="hypertrophy">Ipertrofia</option>
              <option value="endurance">Resistenza</option>
              <option value="flexibility">Flessibilità</option>
              <option value="general">Generale</option>
            </select>
          </div>

          {/* Preferiti toggle */}
          <div className="mt-4 flex items-center">
            <input
              type="checkbox"
              id="favorites"
              checked={showFavoritesOnly}
              onChange={(e) => setShowFavoritesOnly(e.target.checked)}
              className="h-4 w-4 text-indigo-600 rounded"
            />
            <label htmlFor="favorites" className="ml-2 text-sm text-gray-700 dark:text-white/85">
              Mostra solo preferiti
            </label>
          </div>
        </div>
      </div>

      {/* Lista Template */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
          <span className="ml-3 text-gray-600 dark:text-white/65">Caricamento template...</span>
        </div>
      ) : templates.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(template => (
            <TemplateCard
              key={template.id}
              template={template}
              onEdit={handleEdit}
              onDelete={handleDelete}
              onPreview={handlePreview}
              onStats={handleStats}
              onDuplicate={handleDuplicate}
              onAssign={handleAssign}
              onToggleFavorite={handleToggleFavorite}
              getDifficultyBadge={getDifficultyBadge}
              getGoalIcon={getGoalIcon}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-12">
          <Dumbbell className="h-16 w-16 text-gray-400 dark:text-gray-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
            {searchQuery || filterGroup || filterDifficulty || filterGoal
              ? 'Nessun template trovato'
              : 'Nessun template disponibile'}
          </h3>
          <p className="text-gray-500 dark:text-white/65 mb-6">
            {searchQuery || filterGroup || filterDifficulty || filterGoal
              ? 'Prova a modificare i filtri di ricerca'
              : 'Crea il primo template della tua palestra'}
          </p>
          {!searchQuery && !filterGroup && !filterDifficulty && !filterGoal && (
            <button
              onClick={(e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setModalOrigin({ 
                  x: rect.left + rect.width / 2, 
                  y: rect.top + rect.height / 2 
                });
                setShowCreateModal(true);
              }}
              className="px-6 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors"
            >
              <Plus className="h-5 w-5 mr-2 inline" />
              Crea Primo Template
            </button>
          )}
        </div>
      )}

      {/* Modali */}
      <IOSModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        origin={modalOrigin}
        size="full"
        title="Nuovo Template"
        className="
          !w-[90vw]
          !h-[90vh]
          !max-w-[90vw]
          !max-h-[90vh]
        "
      >
        {showCreateModal && (
          <CreateTemplateModal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            onSuccess={handleTemplateCreated}
            hideHeader={true}
          />
        )}
      </IOSModal>

      {showEditModal && selectedTemplate && (
        <EditTemplateModal
          isOpen={showEditModal}
          template={selectedTemplate}
          onClose={() => {
            setShowEditModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={handleTemplateUpdated}
        />
      )}

      {showPreviewModal && selectedTemplate && (
        <TemplatePreviewModal
          isOpen={showPreviewModal}
          template={selectedTemplate}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      {showStatsModal && selectedTemplate && (
        <TemplateStatsModal
          isOpen={showStatsModal}
          template={selectedTemplate}
          onClose={() => {
            setShowStatsModal(false);
            setSelectedTemplate(null);
          }}
        />
      )}

      {showAssignModal && selectedTemplate && (
        <AssignTemplateModal
          isOpen={showAssignModal}
          template={selectedTemplate}
          onClose={() => {
            setShowAssignModal(false);
            setSelectedTemplate(null);
          }}
          onSuccess={handleAssignSuccess}
        />
      )}
    </div>
  );
}

// =====================================================
// COMPONENTE: Card Template
// =====================================================
function TemplateCard({ 
  template, 
  onEdit, 
  onDelete, 
  onPreview, 
  onStats, 
  onDuplicate,
  onAssign,
  onToggleFavorite,
  getDifficultyBadge,
  getGoalIcon
}) {
  const { isDark } = useTheme();

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 overflow-hidden hover:shadow-md transition-shadow">
      {/* Header */}
      <div className="p-4 border-b border-gray-200 dark:border-white/10">
        <div className="flex items-start justify-between mb-2">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-2">
              <h3 className="font-semibold text-gray-900 dark:text-white">
                {template.name}
              </h3>
              {template.is_favorite === 1 && (
                <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
              )}
            </div>
            {template.description && template.description !== '0' && template.description.trim() !== '' && (
              <p className="text-sm text-gray-600 dark:text-white/65 line-clamp-2">
                {template.description}
              </p>
            )}
          </div>
        </div>

        {/* Badges */}
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {getDifficultyBadge(template.difficulty_level)}
          {/* I gruppi saranno mostrati quando l'API li includerà nella risposta */}
        </div>

        {/* Info */}
        <div className="flex items-center space-x-4 text-sm text-gray-500 dark:text-white/65">
          <div className="flex items-center">
            <Dumbbell className="h-4 w-4 mr-1" />
            {template.exercise_count} es.
          </div>
          <div className="flex items-center">
            <Clock className="h-4 w-4 mr-1" />
            ~{template.estimated_duration} min
          </div>
          <div className="flex items-center">
            {getGoalIcon(template.target_goal)}
          </div>
        </div>
      </div>

      {/* Footer info */}
      <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center justify-between text-xs text-gray-500 dark:text-white/65 mb-3">
          <span>📈 Usato {template.times_used || 0} volte</span>
          <span>da {template.created_by_name}</span>
        </div>

        {/* Azioni */}
        <div className="grid grid-cols-4 gap-2">
          <button
            onClick={() => onPreview(template)}
            className="flex items-center justify-center px-2 py-2 text-sm text-gray-700 dark:text-white/85 bg-white dark:bg-gray-800 hover:bg-gray-100 dark:hover:brightness-110 rounded-lg border border-gray-300 dark:border-gray-700 transition-colors"
          >
            <Eye className="h-4 w-4 mr-1" />
            Vedi
          </button>
          
          <button
            onClick={() => onAssign(template)}
            className="flex items-center justify-center px-2 py-2 text-sm text-green-600 dark:text-green-400 bg-green-50 dark:bg-green-900/20 hover:bg-green-100 dark:hover:bg-green-900/30 rounded-lg transition-colors"
          >
            <Users className="h-4 w-4 mr-1" />
            Assegna
          </button>
          
          <button
            onClick={() => onEdit(template)}
            className="flex items-center justify-center px-2 py-2 text-sm text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/20 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-lg transition-colors"
          >
            <Edit className="h-4 w-4 mr-1" />
            Modifica
          </button>
          
          <button
            onClick={() => onStats(template)}
            className="flex items-center justify-center px-2 py-2 text-sm text-purple-600 dark:text-purple-400 bg-purple-50 dark:bg-purple-900/20 hover:bg-purple-100 dark:hover:bg-purple-900/30 rounded-lg transition-colors"
          >
            <BarChart3 className="h-4 w-4 mr-1" />
            Stats
          </button>
        </div>

        {/* Azioni secondarie */}
        <div className="grid grid-cols-3 gap-2 mt-2">
          <button
            onClick={() => onToggleFavorite(template)}
            className="flex items-center justify-center px-2 py-2 text-xs text-gray-600 dark:text-white/65 hover:text-yellow-600 dark:hover:text-yellow-400 transition-colors"
          >
            <Star className={`h-4 w-4 mr-1 ${template.is_favorite ? 'fill-yellow-400 text-yellow-400' : ''}`} />
            {template.is_favorite ? 'Rimuovi' : 'Preferito'}
          </button>
          
          <button
            onClick={() => onDuplicate(template)}
            className="flex items-center justify-center px-2 py-2 text-xs text-gray-600 dark:text-white/65 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
          >
            <Copy className="h-4 w-4 mr-1" />
            Duplica
          </button>
          
          <button
            onClick={() => onDelete(template)}
            className="flex items-center justify-center px-2 py-2 text-xs text-gray-600 dark:text-white/65 hover:text-red-600 dark:hover:text-red-400 transition-colors"
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Elimina
          </button>
        </div>
      </div>
    </div>
  );
}

// =====================================================
// COMPONENTE: Stat Card
// =====================================================
function StatCard({ icon, label, value, color }) {
  const colors = {
    blue: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
    yellow: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-600 dark:text-yellow-400',
    green: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
    purple: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400'
  };

  return (
    <div className="bg-white dark:bg-gray-900 rounded-lg shadow-sm border border-gray-200 dark:border-white/10 p-4">
      <div className="flex items-center">
        <div className={`p-2 rounded-lg mr-3 ${colors[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-sm font-medium text-gray-600 dark:text-white/65">{label}</p>
          <p className="text-xl font-bold text-gray-900 dark:text-white">{value}</p>
        </div>
      </div>
    </div>
  );
}












